// Shared option lists for the Enrollment Hub tabs. Values match the legacy pages 1:1:
// - Tab 1: src/pages/patientEnrollment/PatientEnrollment.tsx / EnrollmentPatientTable.tsx
// - Tab 2: src/pages/digitalEnrollment/DigitalEnrollment.tsx
// These are backend enums, not PSP-driven config, so they're hardcoded here exactly as
// in the legacy pages (not sourced from pspConfigReducer).

import {FilterItem} from 'src/components-new/data/Filters'

export interface Option {
  value: string
  label: string
}

export interface ColumnConfig {
  id: string
  label: string
}

// Map of id -> label for the columns actually shown (the PSP's columnOptions, or a tab's
// DEFAULT_COLUMNS fallback when it's absent/empty). A Map rather than a Set — unlike
// materialRequestsColumns.getActiveColumnIds — because the filters drawer needs to inherit
// both the visibility *and* the label of the column it searches into.
export function getActiveColumnLabels(
  columnConfig: ColumnConfig[] | undefined,
  defaults: ColumnConfig[]
): Map<string, string> {
  const active = columnConfig && columnConfig.length > 0 ? columnConfig : defaults
  return new Map(active.map(col => [col.id, col.label]))
}

// Keeps a tab's filter config in sync with its columns: a filter only survives if its
// column is active, and it adopts that column's label. `filterToColumnId` covers the one
// case where a filter id doesn't match its column id 1:1 (e.g. the digital enrollment
// "Terapia" filter searches on `therapyTypeId` but the table only ever has a derived
// `therapyName` column).
export function syncFiltersWithColumns(
  filters: FilterItem[],
  columnLabels: Map<string, string>,
  filterToColumnId: Record<string, string> = {}
): FilterItem[] {
  return filters
    .filter(item => columnLabels.has(filterToColumnId[item.id] ?? item.id))
    .map(item => ({...item, label: columnLabels.get(filterToColumnId[item.id] ?? item.id) ?? item.label}))
}

// src/components/templates/EnrollmentPatientTable/EnrollmentPatientTable.tsx lines 31-34
export const PATIENT_ENROLLMENT_STATUS_OPTIONS: Option[] = [
  {value: 'COMPLETED', label: 'Completata'},
  {value: 'WAITING', label: 'In attesa'},
]

// src/pages/digitalEnrollment/DigitalEnrollment.tsx lines 16-21 — CONFIRMED/COMPLETED and
// PATIENT_COMPILED/DOCTOR_COMPILED intentionally share a label: two distinct backend
// states render identically in the table (see getDigitalEnrollmentState{Label,Color}
// below, which look a single row's state up in this flat list).
export const DIGITAL_ENROLLMENT_STATE_OPTIONS: Option[] = [
  {value: 'CONFIRMED', label: 'Completata'},
  {value: 'COMPLETED', label: 'Completata'},
  {value: 'PATIENT_COMPILED', label: 'Inviata al paziente'},
  {value: 'DOCTOR_COMPILED', label: 'Inviata al paziente'},
]

// Filter-only variant of the same domain: one row per user-facing label, each carrying
// every backend value it stands for as a comma-separated string (psp-backend's
// digital-enrollment GET/count endpoints split this on "," and match with a Mongo $in —
// see get/handler.js). A plain `Option[]` (one entry per backend value) would render two
// visually-identical, unlabeled "Completata"/"Inviata al paziente" rows in the filter's
// Select — the doctor has no way to tell them apart, and doesn't need to: the table
// already collapses them to the same label/color.
export const DIGITAL_ENROLLMENT_STATE_FILTER_OPTIONS: Option[] = [
  {value: 'CONFIRMED,COMPLETED', label: 'Completata'},
  {value: 'PATIENT_COMPILED,DOCTOR_COMPILED', label: 'Inviata al paziente'},
]

export function getPatientEnrollmentStatusLabel(status?: string): string {
  return PATIENT_ENROLLMENT_STATUS_OPTIONS.find(opt => opt.value === status)?.label ?? (status ?? '—')
}

export function getPatientEnrollmentStatusColor(status?: string): 'success' | 'warning' | 'default' {
  switch (status) {
    case 'COMPLETED':
      return 'success'
    case 'WAITING':
      return 'warning'
    default:
      return 'default'
  }
}

export function getDigitalEnrollmentStateLabel(state?: string): string {
  return DIGITAL_ENROLLMENT_STATE_OPTIONS.find(opt => opt.value === state)?.label ?? (state ?? '—')
}

export function getDigitalEnrollmentStateColor(state?: string): 'success' | 'info' | 'default' {
  switch (state) {
    case 'CONFIRMED':
    case 'COMPLETED':
      return 'success'
    case 'PATIENT_COMPILED':
    case 'DOCTOR_COMPILED':
      return 'info'
    default:
      return 'default'
  }
}
